import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { TableChangeType, TableChangeState } from "react-bootstrap-table-next";
import { debounce } from "lodash";
import { ClientError } from "graphql-request";
import { Row } from "react-table";

import buildColumns from "./Columns";
import { IRootReducer } from "../../../reducers";
import { Key } from "../Key";
import { keysFetched } from "../../../actions/keysActions";
import { KeysPaginationParams } from "../../../providers/BlockchainNetworkProvider/DataServiceReadService";
import useDataServiceTableHandler from "../../../hooks/useDataServiceTableHandler";
import { searchToSearchQuery } from "../../../helpers/helpers";
import renderTable from "../../UI/MUITable/Renderer";
import { useBlockchainNetwork } from "../../../providers/BlockchainNetworkProvider/BlockchainNetworkProvider";
import keyRowFormatter from "../helpers/keyRowFormatter";
import { useLayout } from "../../../providers/LayoutProvider/LayoutProvider";
import KeysTableHeader from "./Header";
import { useOrgManager } from "../../../providers/OrgManagerProvider/OrgManagerProvider";
import useSnackbarNotifications from "../../../hooks/useSnackbarNotifications";

const Renderer = (props: { rows: Key[]; fetchKeys: () => any }) => {
  const [selectedRows, setSelectedRows] = useState<Key[]>([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dsReadService } = useBlockchainNetwork();
  const { meta } = useSelector((state: IRootReducer) => state.keys);
  const { organisationAddress } = useOrgManager();
  const snackbar = useSnackbarNotifications();
  const { theme } = useLayout();

  const { lastAction, queryParams, tableChanged } =
    useDataServiceTableHandler<Key>({
      defaults: {
        order: "asc",
        orderBy: "name",
      },
    });

  // Fetch records with current parameters
  //
  const fetchKeys = useCallback(
    (params: KeysPaginationParams) => {
      if (!params.filter?.owner || !dsReadService) return;

      dsReadService
        .getKeys(params)
        .request.then(({ keys, meta }) => {
          dispatch(keysFetched(keys, meta));
        })
        .catch((error: ClientError) => {
          if (!error.response.errors) return;

          snackbar.error(
            t("messages.keys.fetchFailed", {
              message: error.response.errors
                .map((e: any) => e.message)
                .join(", "),
            })
          );
        });
    },
    [t, dispatch, dsReadService, snackbar]
  );

  // useEffect to run when search is changed
  //
  useEffect(() => {
    if (lastAction !== "searchChanged") return;

    const params = {
      ...queryParams,
      search: searchToSearchQuery(queryParams.search),
      filter: { owner: organisationAddress },
    };

    const debounceFetch = debounce(() => {
      fetchKeys(params);
    }, 500);

    debounceFetch();

    return () => {
      debounceFetch.cancel();
    };
  }, [lastAction, fetchKeys, organisationAddress, queryParams]);

  // useEffect to run when search is NOT changed
  //
  useEffect(() => {
    if (lastAction !== "searchNotChanged") return;

    const params = {
      ...queryParams,
      search: searchToSearchQuery(queryParams.search),
      filter: { owner: organisationAddress },
    };

    fetchKeys(params);
  }, [lastAction, fetchKeys, organisationAddress, queryParams]);

  // Event to run when any table attribute changes
  const handleTableChange = (
    type: TableChangeType,
    e: TableChangeState<Key> & { searchText?: string }
  ): void => {
    if (!organisationAddress) return;

    tableChanged(e);
  };

  // Formatting of table rows
  const rowFormatter = (row: Row<Key>) => {
    const key = row.original;

    return keyRowFormatter(key, theme);
  };

  const onSelectionChange = useCallback(
    (indexes: number[]) => {
      setSelectedRows(indexes.map((i) => props.rows[i]));
    },
    [props.rows]
  );

  return renderTable({
    mode: "server",
    data: props.rows,
    columns: buildColumns(t),
    options: {
      useSelect: true,
      searchInput: true,
      searchInputLabel: t("tables.search.placeholder"),
      header: (
        <KeysTableHeader
          selectedRows={selectedRows}
          fetchKeys={props.fetchKeys}
        />
      ),
    },
    onTableChange: handleTableChange as any,
    onSelectionChange,
    state: {
      page: queryParams.page,
      sizePerPage: queryParams.perPage,
      totalSize: meta.total,
      orderBy: queryParams.orderBy,
      order: queryParams.order,
    },
    rowFormatter,
  });
};

export default Renderer;
